import React, { useEffect, useRef, useState } from "react";
import styles from "./patch-ticket-assignee-dropdown.module.module.scss";
import SpinnerWithVariant from "../SpinnerWithVariant/spinner-with-variant";
import { SupportAgentTicket } from "@src/types/support";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { getSupportAgentsUseCase } from "@src/cloud-connect/user-manager/use-cases/get-support-agents-use-case";
import { ZitadelUser } from "@src/types/user-management";
import InitialsAvatar from "../initials-avatar/initials-avatar";
import { patchTicketAssigneeUseCase } from "@src/cloud-connect/support/use-cases/patch-ticket-assignee-use-case";
import { useI18N } from "@src/language/language-context";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";

const cssClassName = "patch-ticket-assignee-dropdown";

interface PatchTicketPriorityDropdownPropTypes {
  ticket: SupportAgentTicket;
  disabled?: boolean;
  disabledTooltip?: string;
  // the PATCH response returns the updated ticket; without this callback the
  // parent keeps its stale copy (assigned_to === null) and the status dropdown
  // wrongly blocks "solved" until a full page reload
  onTicketUpdated?: (ticket: SupportAgentTicket) => void;
}

interface AssigneeType {
  id: string;
  name: string;
  email: string;
}

export const PatchTicketAssigneesDropdown = ({
  ticket,
  disabled = false,
  disabledTooltip = "",
  onTicketUpdated,
}: PatchTicketPriorityDropdownPropTypes) => {
  const i18n = useI18N();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<AssigneeType | null>(null);
  const [possibleAssignees, setPossibleAssignees] = useState<
    AssigneeType[] | null
  >(null);
  const [isChangingAssignee, setIsChangingAssignee] = useState(false);

  const popoverRef = useRef(null);

  useClickOutsideListener(popoverRef, () => setOpen(false), open);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const texts = {
    selectAssigneeLabel: i18n(
      "modules.patchTicketAssigneesDropdown.selectAssigneeLabel",
    ),
    noAssigneesAvailable: i18n(
      "modules.patchTicketAssigneesDropdown.noAssigneesAvailable",
    ),
    errorUpdatingAssignee: i18n(
      "modules.patchTicketAssigneesDropdown.errorUpdatingAssignee",
    ),
    errorFetchingAssignees: i18n(
      "modules.patchTicketAssigneesDropdown.errorFetchingAssignees",
    ),
    successUpdatingAssignee: i18n(
      "modules.patchTicketAssigneesDropdown.successUpdatingAssignee",
    ),
  };

  const handleSelect = async (assignee: AssigneeType) => {
    if (disabled || isChangingAssignee || selected?.id === assignee.id) return;
    setIsChangingAssignee(true);
    // assigning a human takes over from the AI: clear the flag in the same PATCH
    const response = await patchTicketAssigneeUseCase(
      ticket.id,
      assignee.id,
      undefined,
      {
        handledByAiChatbot: false,
      },
    );

    if (!response.success) {
      setIsChangingAssignee(false);
      setOpen(false);
      errorSnackbar(texts.errorUpdatingAssignee);
    } else {
      setSelected(assignee);
      setIsChangingAssignee(false);
      setOpen(false);
      successSnackbar(texts.successUpdatingAssignee);
      if (onTicketUpdated && response.data) {
        onTicketUpdated(response.data);
      }
    }
  };

  const returnTextForNoAssignees = () => {
    if (possibleAssignees && possibleAssignees.length === 0) {
      return texts.noAssigneesAvailable;
    }
    return texts.selectAssigneeLabel;
  };

  useEffect(() => {
    const getPossibleAssignees = async () => {
      const assigneesResponse = await getSupportAgentsUseCase();

      if (!assigneesResponse.success) {
        errorSnackbar(texts.errorFetchingAssignees);
        setPossibleAssignees([]);
      } else {
        const assignees: ZitadelUser[] = assigneesResponse.data;
        const formattedAssignees = assignees.map((assignee) => ({
          id: assignee.id,
          name: `${assignee.given_name} ${assignee.family_name}`,
          email: assignee.email,
        }));

        setPossibleAssignees(formattedAssignees);
      }
    };

    getPossibleAssignees();
  }, [ticket]);

  useEffect(() => {
    if (ticket.assigned_to && ticket.assigned_to.id) {
      setSelected({
        id: ticket.assigned_to.id,
        name: ticket.assigned_to.name,
        email: ticket.assigned_to.email,
      });
    } else {
      setSelected({ id: "", name: "", email: "" });
    }
  }, [ticket]);

  return (
    <div ref={popoverRef} className={styles[cssClassName]}>
      <CustomTooltip
        content={disabled ? disabledTooltip : ""}
        className={styles[`${cssClassName}__tooltip-wrapper`]}
      >
        <button
          type="button"
          className={`${styles[`${cssClassName}__selector`]} ${
            disabled && disabledTooltip
              ? styles[`${cssClassName}__selector--sse-disabled`]
              : ""
          }`}
          onClick={() => setOpen(!open)}
          aria-haspopup="listbox"
          aria-expanded={open && !disabled}
          disabled={
            disabled || !possibleAssignees?.length || isChangingAssignee
          }
        >
          {!selected || !possibleAssignees || isChangingAssignee ? (
            <SpinnerWithVariant variant={["small", "secondary"]} />
          ) : (
            <FlexRowContainer>
              {selected && selected.name && (
                <InitialsAvatar name={selected.name} />
              )}
              {selected && selected.id
                ? selected.name
                : returnTextForNoAssignees()}
            </FlexRowContainer>
          )}
        </button>
      </CustomTooltip>

      {open && !disabled && (
        <ul
          className={styles[`${cssClassName}__list`]}
          role="listbox"
          aria-activedescendant={selected ? selected.id : undefined}
          tabIndex={0}
        >
          {possibleAssignees?.map((assignee) => (
            <li
              key={assignee.id}
              id={assignee.id}
              role="option"
              aria-selected={selected ? selected.id === assignee.id : false}
            >
              <button
                type="button"
                className={styles[`${cssClassName}__item`]}
                onClick={() => handleSelect(assignee)}
                disabled={disabled || isChangingAssignee}
              >
                <InitialsAvatar name={assignee.name} />
                {assignee.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
